import { Add, Cancel, Delete, Done, Edit, Undo } from "@mui/icons-material";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Toolbar, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TicketApiService from "../../../adapters/xhr/TicketApiService";
import authService from "../../../components/api-authorization/AuthorizeService";
import { MyDropdown } from "../../../components/my-dropdown/MyDropdown";
import { TransportationMethod, UserRoles } from "../../../Constant";
import SnackbarUtils from "../../../SnackbarUtils";
import { MyTextField } from "../../../components/my-text-field/MyTextField";

const TicketTypes = [
  "SINGLE_TICKET_APP",
  "SINGLE_TICKET_TRAVEL_FUND",
  "SINGLE_TICKET_BANK_CARD",
  "PERIOD_TICKET_24H",
  "PERIOD_TICKET_72H",
  "PERIOD_TICKET_FLEX_10_40",
  "PERIOD_TICKET_30_DAY",
  "MOVINGO_30_DAY_URBAN_TRAVEL",
  "MOVINGO_30_DAY_REGIONAL_TRAVEL",
  "MOVINGO_90_DAY",
  "MOVINGO_ANNUAL",
];

export const TicketSection = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [previousRows, setPreviousRows] = useState([]);
    const isReadOnly = authService._user?.role == UserRoles.CaseManager;
    const hasAddRow = rows.some((item) => item.isAddMode);
    const hasEditRow = rows.some((item) => item.isEditMode);
    const transportationMethodOptions = Object.values(TransportationMethod);

    const getDefaultRule = () => ({
        FixedAddonSek: "",
        TransportationMethods: [],
    });

    const normalizeRule = (rule) => {
        const toTransportationMethods = (methods) => {
            if (Array.isArray(methods)) {
                return methods.filter((item) => item != null && item !== "");
            }

            if (typeof methods === "string") {
                return methods
                    .split(",")
                    .map((item) => item.trim())
                    .filter((item) => item !== "");
            }

            return [];
        };

        if (!rule) {
            return getDefaultRule();
        }

        let parsedRule = rule;

        if (typeof parsedRule === "string") {
            try {
                parsedRule = JSON.parse(parsedRule);
            } catch {
                return getDefaultRule();
            }
        }

        if (Array.isArray(parsedRule)) {
            parsedRule = parsedRule[0];
        }

        if (!parsedRule || typeof parsedRule !== "object") {
            return getDefaultRule();
        }

        return {
            FixedAddonSek: parsedRule.FixedAddonSek ?? parsedRule.fixedAddonSek ?? "",
            TransportationMethods: toTransportationMethods(parsedRule.TransportationMethods ?? parsedRule.transportationMethods),
        };
    };

    const convertTransportationMethodsToString = (methods) => methods.join(", ");
    const serializeRule = (rule, asArray = false) => {
        const normalizedRule = normalizeRule(rule);
        return JSON.stringify(asArray ? [normalizedRule] : normalizedRule);
    };
    const isRuleValid = (rule) => {
        const normalizedRule = normalizeRule(rule);
        return normalizedRule.FixedAddonSek !== "" && normalizedRule.TransportationMethods.length > 0;
    };
    const getRuleDisplayValue = (rule) => {
        const normalizedRule = normalizeRule(rule);
        return `FixedAddonSek: ${normalizedRule.FixedAddonSek}, TransportationMethods: ${convertTransportationMethodsToString(normalizedRule.TransportationMethods)}`;
    };

    const columns = [
        { id: "TicketType", label: t("ticket_type"), minWidth: 400, width: 400, editable: true },
        { id: "Rule", label: t("rule"), minWidth: 400, width: 400, editable: true },
        { id: "Actions", label: t("actions"), minWidth: 170, hidden: isReadOnly },
    ];

    useEffect(() => {
        fetchSettingsAsync();
    }, []);

    const fetchSettingsAsync = async () => {
        const response = await TicketApiService.getAll();
        if (response?.isSuccess) {
            const data = response.data
                .map((item) => ({
                    id: item.id,
                    TicketType: item.ticketType,
                    Rule: normalizeRule(item.rule),
                }));

            setRows(data);
            setPreviousRows(data);
        }
    };

    const onAddRow = () => {
        setRows([{ id: "0", TicketType: "", Rule: getDefaultRule(), isAddMode: true }, ...previousRows]);
    };

    const onEditRow = (id) => {
        setRows(
            previousRows.map((row) => {
                if (row.id === id) {
                    return { ...row, isEditMode: true };
                }

                return row;
            })
        );
    };

    const onChangeRowValues = (value, name, row) => {
        setRows(
            rows.map((item) => {
                if (item.id === row.id) {
                    return { ...item, [name]: value };
                }

                return item;
            })
        );
    };

    const onChangeRuleValue = (value, name, row) => {
        const normalizedRule = normalizeRule(row.Rule);
        const nextValue = name === "TransportationMethods" ? value.split(",").map((item) => item.trim()).filter((item) => item !== "") : value;
        onChangeRowValues({ ...normalizedRule, [name]: nextValue }, "Rule", row);
    };

    const onRevertChanges = () => {
        setRows(previousRows);
    };

    const onSaveSettingsAsync = async (id) => {
        const row = rows.find((item) => item.id === id);

        if (row.TicketType?.trim() === "" || !isRuleValid(row.Rule)) {
            return;
        }

        const payload = {
            ticketType: row.TicketType,
            rule: serializeRule(row.Rule, true),
        };

        const response = await TicketApiService.update(row.TicketType, payload);
        if (!response || !response.isSuccess) {
            SnackbarUtils.error(t(t(response?.errorMessage)));
            onRevertChanges();
            return;
        }

        await fetchSettingsAsync();
    };

    const onDeleteSettingsAsync = async (id) => {
        const response = await TicketApiService.deleteById(id);
        if (!response?.isSuccess) {
            SnackbarUtils.error(t(response?.errorMessage));
            return;
        }

        await fetchSettingsAsync();
    };

    const onAddSettingsAsync = async (id) => {
        const row = rows.find((item) => item.id === id);

        if (rows.find((item) => item.id != row.id && item.TicketType == row.TicketType) != null) {
            SnackbarUtils.error(t("ticket_for_title_existed", { title: row.TicketType }));
            return;
        }

        if (row.TicketType?.trim() === "" || !isRuleValid(row.Rule)) {
            return;
        }

        const payload = {
            ticketType: row.TicketType,
            rule: serializeRule(row.Rule),
        };

        const response = await TicketApiService.add(payload);
        if (!response?.isSuccess) {
            SnackbarUtils.error(t(response?.errorMessage));
            return;
        }

        await fetchSettingsAsync();
    };

    return (
        <>
            <Toolbar>
                <Typography variant="h6" id="tableTitle" component="div">
                    {t("ticket")}
                </Typography>
                {!hasAddRow && !isReadOnly && <Add className="selector margin left-1 top-0 bottom-0" onClick={() => onAddRow()} />}
                {hasAddRow && <Cancel className="selector margin left-1 top-0 bottom-0" onClick={() => onRevertChanges()} />}
            </Toolbar>
            <TableContainer sx={{ maxHeight: 440 }}>
                <Table stickyHeader aria-label="sticky table" size="small">
                    <TableHead>
                        <TableRow>
                            {columns
                                .filter((item) => !item.hidden)
                                .map((column, key) => (
                                    <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                                        {column.label}
                                    </TableCell>
                                ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, key) => {
                            return (
                                <TableRow hover tabIndex={-1} key={key}>
                                    {columns
                                        .filter((item) => !item.hidden)
                                        .map((column) => {
                                            if (column.id === "Actions") {
                                                return (
                                                    <TableCell key={100} align="left">
                                                        {!row.isEditMode && !hasEditRow && !hasAddRow && (
                                                            <>
                                                                <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onEditRow(row.id)} />
                                                                <Delete className="selector margin top-0 bottom-0" onClick={() => onDeleteSettingsAsync(row.id)} />
                                                            </>
                                                        )}
                                                        {row.isEditMode && (
                                                            <>
                                                                <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveSettingsAsync(row.id)} />
                                                                <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertChanges()} />
                                                            </>
                                                        )}
                                                        {row.isAddMode && <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onAddSettingsAsync(row.id)} />}
                                                    </TableCell>
                                                );
                                            }

                                            const value = row[column.id];

                                            if (row.isAddMode || (row.isEditMode && column.editable)) {
                                                return (
                                                    <TableCell key={column.id} align={column.align}>
                                                        {column.id === "TicketType" &&
                                                            <MyDropdown name={column.id} size="small" 
                                                            options={TicketTypes} 
                                                            value={value} onChange={(e, value) => onChangeRowValues(value, column.id, row)} />
                                                        }
                                                        {column.id === "Rule" &&
                                                            <>
                                                                <MyTextField
                                                                    name="FixedAddonSek"
                                                                    size="small"
                                                                    label="FixedAddonSek"
                                                                    value={normalizeRule(value).FixedAddonSek}
                                                                    type="number"
                                                                    onChange={(nextValue) => onChangeRuleValue(nextValue, "FixedAddonSek", row)}
                                                                />
                                                                <MyTextField
                                                                    name="TransportationMethods"
                                                                    size="small"
                                                                    label="TransportationMethods"
                                                                    value={convertTransportationMethodsToString(normalizeRule(value).TransportationMethods)}
                                                                    placeholder={transportationMethodOptions.join(", ")}
                                                                    onChange={(nextValue) => onChangeRuleValue(nextValue, "TransportationMethods", row)}
                                                                />
                                                            </>
                                                        }
                                                    </TableCell>
                                                );
                                            }

                                            return (
                                                <TableCell key={column.id} align={column.align}>
                                                    {column.id === "Rule"
                                                        ? getRuleDisplayValue(value)
                                                        : column.format && typeof value === "number" ? column.format(value) : value}
                                                </TableCell>
                                            );
                                        })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};